import React, { useState, useMemo, useEffect } from "react";
import EmptyState from "../common/EmptyState";
import api from "../../api";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import {
  ChevronDown, ChevronUp, FileText, Search,
  Truck, Edit2, Trash2, Copy, Send, CheckCircle2,
  Package, Calendar, User, Euro, Wallet, TrendingUp, Settings,
} from "lucide-react";
import { toast } from "react-toastify";

const toDateSafe = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateParts = (val) => {
  const d = toDateSafe(val);
  if (!d) return { main: "—", sub: "" };
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  // dd-mm-yyyy, ou dd-mm si l'année en cours.
  const main = yyyy === new Date().getFullYear() ? `${dd}-${mm}` : `${dd}-${mm}-${yyyy}`;
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0 || /\dT\d{2}:\d{2}/.test(String(val));
  const sub = hasTime ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
  return { main, sub };
};

const TypeBadge = ({ type, t }) => {
  const isMoving =
    String(type || "").toLowerCase() === "moving" ||
    String(type || "").toLowerCase().includes("demen");
  return isMoving ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-sky-500/10 text-sky-500 border border-sky-500/20 whitespace-nowrap">
      🚚 {t("mission.form.moving", "Déménagement")}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 whitespace-nowrap">
      📦 {t("mission.form.delivery", "Livraison")}
    </span>
  );
};

const euro = (v) => `${Number(v || 0).toLocaleString("fr-FR")} €`;

const MissionList = ({ missions, onEdit, onRefresh }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn]   = useState("date");
  const [sortOrder, setSortOrder]     = useState("desc");
  const [search, setSearch]           = useState("");
  const [confirmId, setConfirmId]     = useState(null);
  const [subcontractors, setSubcontractors] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [sendTarget, setSendTarget]   = useState(null); // mission en cours d'envoi
  const [sendRecipientIds, setSendRecipientIds] = useState([]);
  const [sending, setSending]         = useState(false);
  const perPage = 20;

  useEffect(() => {
    api.get("/subcontractors").then((r) => setSubcontractors(r.data || [])).catch(() => {});
    api.get("/settings/team-leaders").then((r) => setTeamLeaders(r.data || [])).catch(() => {});
  }, []);

  const subcontractorName = (id) => subcontractors.find((s) => s.id === id)?.name;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return missions.filter(m =>
      `${m.type} ${m.client} ${m.address}`.toLowerCase().includes(q)
    );
  }, [missions, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av = a[sortColumn], bv = b[sortColumn];
      if (sortColumn === "date") {
        av = toDateSafe(av) || new Date(0);
        bv = toDateSafe(bv) || new Date(0);
      }
      if (av < bv) return sortOrder === "asc" ? -1 : 1;
      if (av > bv) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortColumn, sortOrder]);

  const totalPages = Math.ceil(sorted.length / perPage);
  const page       = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleSort = (col) => {
    if (sortColumn === col) setSortOrder(o => o === "asc" ? "desc" : "asc");
    else { setSortColumn(col); setSortOrder("asc"); }
  };

  const SortIcon = ({ col }) =>
    sortColumn === col
      ? (sortOrder === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />)
      : null;

  const handleDelete = (id) => setConfirmId(id);

  const confirmDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      await api.delete(`/missions/${id}`);
      toast.success(t("mission.list.deleteSuccess"));
      onRefresh();
    } catch { toast.error(t("mission.list.deleteError")); }
  };

  const handleCopy = async (m) => {
    try {
      await api.post("/missions", {
        type: m.type || "Moving", client: m.client || "Client", address: m.address || "",
        date: new Date().toISOString().slice(0, 10),
        priceHT: parseFloat(m.priceHT) || 0,
        priceTTC: (parseFloat(m.priceHT) || 0) * 1.2,
        charges: parseFloat(m.charges) || 0,
        employees: (m.employees || []).filter(e => e.employee_name?.trim())
          .map(e => ({ employee_name: e.employee_name.trim(), salary: parseFloat(e.salary) || 0 })),
      });
      toast.success(t("mission.list.copySuccess") || "Mission copiée !");
      onRefresh();
    } catch { toast.error("Erreur lors de la copie."); }
  };

  const handleWaybill = async (id) => {
    try {
      const r = await api.get(`/waybill/${id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank", "noopener");
    } catch { toast.error(t("mission.list.waybillError")); }
  };

  // Destinataires identifiés par clé préfixée : "s_<id>" sous-traitant, "l_<id>" chef d'équipe.
  const openSendWaybill = (mission) => {
    setSendTarget(mission);
    let previousIds = [];
    if (mission.waybill_sent_to_ids) {
      try {
        previousIds = JSON.parse(mission.waybill_sent_to_ids);
      } catch {
        previousIds = [];
      }
    } else if (mission.waybill_sent_to) {
      previousIds = [`s_${mission.waybill_sent_to}`];
    }
    setSendRecipientIds(previousIds);
  };

  const toggleSendRecipient = (id) => {
    setSendRecipientIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const confirmSendWaybill = async () => {
    if (!sendTarget || sendRecipientIds.length === 0) return;
    setSending(true);
    try {
      const subcontractor_ids = sendRecipientIds.filter((id) => id.startsWith("s_")).map((id) => Number(id.slice(2)));
      const team_leader_ids = sendRecipientIds.filter((id) => id.startsWith("l_")).map((id) => id.slice(2));
      await api.post(`/waybill/${sendTarget.id}/send`, { subcontractor_ids, team_leader_ids });
      toast.success(t("mission.list.waybillSent", "Lettre de voiture envoyée."));
      setSendTarget(null);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("mission.list.waybillSendError", "Échec de l'envoi de la lettre de voiture."));
    } finally {
      setSending(false);
    }
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(missions), "Missions");
    XLSX.writeFile(wb, "missions.xlsx");
    toast.success(t("mission.list.exportSuccess"));
  };

  // Colonnes condensées : prix (TTC/HT), coûts (salaire/charges) et
  // adresse/employés regroupés sous le client → plus de scroll horizontal.
  const COLS = [
    { key: "type",     label: t("mission.list.type"),    align: "left",  icon: Package },
    { key: "date",     label: t("mission.list.date"),    align: "left",  icon: Calendar },
    { key: "client",   label: t("mission.list.client"),  align: "left",  icon: User },
    { key: "priceTTC", label: t("mission.list.priceTTC", "Prix"),  align: "right", icon: Euro },
    { key: "salary",   label: t("mission.list.salary", "Coûts"),   align: "right", icon: Wallet },
    { key: "profit",   label: t("mission.list.profit"),  align: "right", icon: TrendingUp },
  ];

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center text-orange-500">
            <Truck size={16} />
          </div>
          <h2 className="text-base font-semibold text-base-content">{t("mission.list.title")}</h2>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-base-300 text-base-content/60">
            {sorted.length}
          </span>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none sm:w-52">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              placeholder={t("mission.list.searchPlaceholder")}
              className="input input-bordered input-sm pl-8 w-full bg-base-100 border-base-200 focus:border-primary focus:outline-none text-base-content/80 placeholder:text-base-content/50"
            />
          </div>
          <button
            onClick={exportToExcel}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-base-200 bg-base-100 text-base-content/70 text-sm font-medium hover:bg-base-200 hover:border-base-200 transition-colors"
          >
            <FileText size={14} /> {t("mission.list.export")}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-base-200 bg-base-100 shadow-sm">
        <table className="table table-sm w-full">
          <thead>
            <tr className="bg-base-200 border-b border-base-200">
              {COLS.map(({ key, label, align, icon: Icon }) => (
                <th
                  key={key}
                  className={`cursor-pointer select-none text-base-content/60 text-[11px] font-semibold tracking-wider uppercase py-3 hover:text-base-content/80 transition-colors whitespace-nowrap ${align === "right" ? "text-right" : ""}`}
                  onClick={() => handleSort(key)}
                >
                  <span className={`flex items-center gap-1.5 ${align === "right" ? "justify-end" : ""}`}>
                    <Icon size={12} className="text-base-content/35" />
                    {label}
                    <span className="text-base-content/30"><SortIcon col={key} /></span>
                  </span>
                </th>
              ))}
              <th className="text-center text-base-content/60 text-[11px] font-semibold tracking-wider uppercase py-3">
                <span className="flex items-center justify-center gap-1.5">
                  <Settings size={12} className="text-base-content/35" />
                  {t("mission.list.actions")}
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-200">
            {page.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState type="missions" />
                </td>
              </tr>
            ) : page.map((m, i) => {
              const { main, sub } = formatDateParts(m.date);
              const profit = Number(m.profit || 0);
              const employeesLabel = Array.isArray(m.employees)
                ? m.employees.map(e => e.employee_name).filter(Boolean).join(", ")
                : m.employee || "";
              const route = (m.departure || m.arrival)
                ? `${m.departure || "?"} → ${m.arrival || "?"}`
                : m.address;
              return (
                <tr
                  key={m.id || i}
                  className="hover:bg-base-200 transition-colors group"
                >
                  <td className="py-3"><TypeBadge type={m.type} t={t} /></td>
                  <td className="py-3">
                    <div className="text-sm font-medium text-base-content whitespace-nowrap">{main}</div>
                    <div className="text-xs text-base-content/50 capitalize">{sub}</div>
                  </td>
                  <td className="py-3 max-w-[220px]">
                    <div className="flex items-center gap-1.5">
                      <div className="font-semibold text-base-content text-sm truncate">{m.client}</div>
                      {m.external_source === "desbrasenplus" && (
                        <span
                          className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500/10 text-orange-600 border border-orange-500/20"
                          title={m.external_id ? `Des Bras en Plus · #${m.external_id}` : "Importé de Des Bras en Plus"}
                        >
                          DBEP
                        </span>
                      )}
                      {m.waybill_sent_at && (
                        <span
                          className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20"
                          title={`${t("mission.list.waybillSentTo", "Envoyée à")} ${subcontractorName(m.waybill_sent_to) || "—"} · ${new Date(m.waybill_sent_at).toLocaleString("fr-FR")}`}
                        >
                          <CheckCircle2 size={10} /> {t("mission.list.waybillSentBadge", "LDV envoyée")}
                        </span>
                      )}
                    </div>
                    {route && <div className="text-xs text-base-content/50 truncate">📍 {route}</div>}
                    {employeesLabel && <div className="text-xs text-base-content/40 truncate">👷 {employeesLabel}</div>}
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    <div className="font-mono text-sm font-semibold text-base-content">{euro(m.priceTTC)}</div>
                    <div className="font-mono text-xs text-base-content/45">{euro(m.priceHT)} {t("mission.list.netTag")}</div>
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    <div className="font-mono text-sm text-base-content/70">{euro(m.salary)}</div>
                    <div className="font-mono text-xs text-base-content/45">+{euro(m.charges)} {t("mission.list.chargesTag")}</div>
                  </td>
                  <td className="py-3 text-right">
                    <span
                      className={`font-bold font-mono text-sm whitespace-nowrap ${
                        profit >= 0 ? "text-emerald-600" : "text-red-500"
                      }`}
                    >
                      {euro(profit)}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ActionBtn onClick={() => onEdit(m)} title={t("mission.list.edit")}>
                        <Edit2 size={13} />
                      </ActionBtn>
                      <ActionBtn onClick={() => handleWaybill(m.id)} title={t("mission.list.waybill")}>
                        <Truck size={13} />
                      </ActionBtn>
                      <ActionBtn onClick={() => openSendWaybill(m)} title={t("mission.list.sendWaybill", "Envoyer la lettre de voiture")}>
                        <Send size={13} />
                      </ActionBtn>
                      <ActionBtn onClick={() => handleCopy(m)} title={t("mission.list.copy")}>
                        <Copy size={13} />
                      </ActionBtn>
                      {m.id && (
                        <ActionBtn onClick={() => handleDelete(m.id)} title={t("mission.list.delete")} danger>
                          <Trash2 size={13} />
                        </ActionBtn>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirm Delete Modal */}
      {confirmId && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-xl border border-base-200 shadow-2xl p-6 max-w-sm w-full animate-fade-in">
            <h3 className="font-bold text-base-content text-base mb-1">{t("mission.list.confirmDelete")}</h3>
            <p className="text-sm text-base-content/50 mb-5">{t("mission.deleted", "Cette action est irréversible.")}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmId(null)} className="btn btn-ghost btn-sm">{t("buttons.cancel")}</button>
              <button onClick={confirmDelete} className="btn btn-error btn-sm">{t("buttons.delete")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Envoi lettre de voiture */}
      {sendTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-xl border border-base-200 shadow-2xl p-6 max-w-sm w-full animate-fade-in">
            <h3 className="font-bold text-base-content text-base mb-1">
              {t("mission.list.sendWaybillTitle", "Envoyer la lettre de voiture")}
            </h3>
            <p className="text-sm text-base-content/50 mb-4">
              {t("mission.list.sendWaybillDesc", "Mission")} #{sendTarget.id} — {sendTarget.client}
            </p>
            <p className="text-xs font-semibold text-base-content/60 mb-2">
              {t("mission.list.selectRecipients", "Destinataires (chef d'équipe interne ou sous-traitant)")}
            </p>
            <div className="max-h-56 overflow-y-auto border border-base-300 rounded-lg mb-5 divide-y divide-base-200">
              {teamLeaders.map((l) => (
                <label
                  key={`l_${l.id}`}
                  className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-base-200"
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm checkbox-primary"
                    checked={sendRecipientIds.includes(`l_${l.id}`)}
                    onChange={() => toggleSendRecipient(`l_${l.id}`)}
                  />
                  <span className="flex-1">{l.name}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                    {t("subcontractors.typeInternal", "Chef d'équipe interne")}
                  </span>
                </label>
              ))}
              {subcontractors.filter((s) => s.active).map((s) => (
                <label
                  key={`s_${s.id}`}
                  className={`flex items-center gap-2 px-3 py-2 text-sm ${!s.email ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-base-200"}`}
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm checkbox-primary"
                    checked={sendRecipientIds.includes(`s_${s.id}`)}
                    disabled={!s.email}
                    onChange={() => toggleSendRecipient(`s_${s.id}`)}
                  />
                  <span className="flex-1">{s.name}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-600">
                    {t("subcontractors.typeExternal", "Sous-traitant")}
                  </span>
                  {!s.email && <span className="text-[10px]">({t("subcontractors.noEmail", "pas d'email")})</span>}
                </label>
              ))}
              {teamLeaders.length === 0 && subcontractors.filter((s) => s.active).length === 0 && (
                <p className="px-3 py-4 text-sm text-base-content/40 italic">
                  {t("mission.list.noRecipients", "Aucun destinataire configuré.")}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSendTarget(null)} className="btn btn-ghost btn-sm">{t("buttons.cancel")}</button>
              <button
                onClick={confirmSendWaybill}
                disabled={sendRecipientIds.length === 0 || sending}
                className="btn btn-primary btn-sm"
              >
                {sending ? t("mission.list.sending", "Envoi…") : t("mission.list.send", "Envoyer")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-base-content/50 text-xs">
            Page {currentPage} sur {totalPages} — {sorted.length} missions
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-lg border border-base-200 bg-base-100 text-base-content/70 text-xs font-medium hover:bg-base-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              ← Préc.
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pg = i + 1;
              return (
                <button
                  key={pg}
                  onClick={() => setCurrentPage(pg)}
                  className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                    currentPage === pg
                      ? "bg-orange-500 text-white border border-orange-500"
                      : "border border-base-200 bg-base-100 text-base-content/70 hover:bg-base-200"
                  }`}
                >
                  {pg}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-lg border border-base-200 bg-base-100 text-base-content/70 text-xs font-medium hover:bg-base-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Suiv. →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionList;

function ActionBtn({ children, onClick, title, danger = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
        danger
          ? "text-base-content/50 hover:text-red-500 hover:bg-red-50"
          : "text-base-content/50 hover:text-base-content hover:bg-base-300"
      }`}
    >
      {children}
    </button>
  );
}
